export type NormalizedVideoQueryResponse = {
  status: 'processing' | 'succeeded' | 'failed';
  progress: number;
  videoUrl: string;
  error: string;
};

function clampProgress(value: unknown): number {
  try {
    if (typeof value === 'string') {
      const match = value.match(/(\d{1,3})/);
      if (match) {
        return Math.max(0, Math.min(100, parseInt(match[1], 10)));
      }
    }

    if (typeof value === 'number') {
      return Math.max(0, Math.min(100, Math.round(value)));
    }
  } catch {
    return 0;
  }

  return 0;
}

function firstResultUrl(results: unknown): string {
  if (!Array.isArray(results) || results.length === 0) {
    return '';
  }

  const first = results[0];
  if (first && typeof first === 'object' && typeof (first as { url?: unknown }).url === 'string') {
    return ((first as { url: string }).url || '').trim();
  }

  return '';
}

export function extractApiErrorMessage(payload: unknown, fallback = '生成失败'): string {
  if (typeof payload === 'string') {
    return payload.trim() || fallback;
  }

  if (!payload || typeof payload !== 'object') {
    return fallback;
  }

  const data = payload as Record<string, unknown>;
  const candidates = [
    data.error,
    data.failure_reason,
    data.message,
    data.msg,
    data.detail,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
    if (candidate && typeof candidate === 'object') {
      const nested = extractApiErrorMessage(candidate, '');
      if (nested) {
        return nested;
      }
    }
  }

  return fallback;
}

export function extractVideoTaskId(payload: unknown): string {
  if (!payload || typeof payload !== 'object') {
    return '';
  }

  const data = payload as Record<string, unknown>;
  if (data.data && typeof data.data === 'object') {
    const innerId = (data.data as { id?: unknown }).id;
    if (typeof innerId === 'string' && innerId.trim()) {
      return innerId.trim();
    }
  }

  const taskId = data.task_id;
  if (typeof taskId === 'string' && taskId.trim()) {
    return taskId.trim();
  }

  const id = data.id;
  if (typeof id === 'string' && id.trim()) {
    return id.trim();
  }

  return '';
}

export function normalizeVideoQueryResponse(payload: unknown): NormalizedVideoQueryResponse {
  const topLevel = payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
  const dataInner =
    topLevel.data && typeof topLevel.data === 'object'
      ? (topLevel.data as Record<string, unknown>)
      : topLevel;

  const rawStatus = String(dataInner.status ?? topLevel.status ?? '').trim().toLowerCase();
  const progress = clampProgress(dataInner.progress ?? dataInner.process ?? topLevel.progress ?? topLevel.process ?? 0);
  const videoUrl = (
    firstResultUrl(dataInner.results) ||
    (typeof dataInner.video_url === 'string' ? dataInner.video_url : '') ||
    (typeof topLevel.video_url === 'string' ? topLevel.video_url : '')
  ).trim();
  const error =
    extractApiErrorMessage(dataInner, '') ||
    extractApiErrorMessage(topLevel, '');

  if (rawStatus === 'completed' || rawStatus === 'succeeded' || rawStatus === 'success') {
    if (!videoUrl) {
      return {
        status: 'failed',
        progress,
        videoUrl: '',
        error: error || '视频生成完成但未返回视频地址',
      };
    }

    return {
      status: 'succeeded',
      progress: progress < 100 ? 100 : progress,
      videoUrl,
      error: '',
    };
  }

  if (
    rawStatus === 'failed' ||
    rawStatus === 'failure' ||
    rawStatus === 'error' ||
    rawStatus === 'cancelled' ||
    rawStatus === 'canceled' ||
    (!!error && rawStatus === '')
  ) {
    return {
      status: 'failed',
      progress,
      videoUrl: '',
      error: error || '生成失败',
    };
  }

  return {
    status: 'processing',
    progress,
    videoUrl: '',
    error: '',
  };
}
